/**
 * Base API client — wraps fetch with canonical envelope handling.
 * All API calls go through /api (proxied to the API server).
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init?.headers },
    ...init,
  });

  const body = (await res.json()) as { success: boolean; data?: T; error?: string; code?: string };

  if (!body.success) {
    throw new ApiError(res.status, body.code, body.error ?? "Unknown error");
  }

  return body.data as T;
}

/**
 * Multipart-friendly variant — used by the department-forms submission and
 * edit endpoints, which are the only routes in the Portal that accept file
 * uploads. The browser sets the `multipart/form-data` boundary header
 * automatically when you hand fetch a `FormData`, so we deliberately do
 * NOT supply a Content-Type here.
 */
async function multipartRequest<T>(
  path: string,
  method: "POST" | "PATCH",
  formData: FormData,
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    method,
    body: formData,
  });
  const body = (await res.json()) as { success: boolean; data?: T; error?: string; code?: string };
  if (!body.success) {
    throw new ApiError(res.status, body.code, body.error ?? "Unknown error");
  }
  return body.data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }),
  put: <T>(path: string, data: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(data) }),
  patch: <T>(path: string, data: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(data) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  postMultipart: <T>(path: string, formData: FormData) =>
    multipartRequest<T>(path, "POST", formData),
  patchMultipart: <T>(path: string, formData: FormData) =>
    multipartRequest<T>(path, "PATCH", formData),
};
